import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  DocumentWithRelations,
  DocumentFilters,
  DocumentSearchResult,
  DocumentUpdateInput,
} from '../types'

const DOCUMENTS_QUERY_KEY = ['documents']

/**
 * Fetch documents with server-side pagination, filtering, and sorting
 */
export function useDocuments(filters: DocumentFilters = {}) {
  const {
    search,
    category,
    document_type,
    tags,
    status = 'active',
    residence_id,
    folder_id,
    owner_id,
    unit_id,
    date_from,
    date_to,
    document_year,
    page = 1,
    page_size = 20,
    sort_by = 'created_at',
    sort_direction = 'desc',
  } = filters

  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'paginated', filters],
    queryFn: async (): Promise<DocumentSearchResult> => {
      // Ensure auth session is loaded
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        return {
          data: [],
          count: 0,
          page,
          page_size,
          total_pages: 0,
        }
      }

      // Start building the query with relations
      let query = supabase
        .from('documents')
        .select(
          `
          *,
          residences:residence_id(id, name, residence_key),
          owners:owner_id(id, first_name, last_name),
          units:unit_id(id, unit_number),
          uploader:created_by(id, first_name, last_name)
        `,
          { count: 'exact' }
        )
        .is('deleted_at', null)

      // Apply filters
      if (status) {
        query = query.eq('status', status)
      }

      if (category) {
        query = query.eq('category', category)
      }

      if (document_type) {
        query = query.eq('document_type', document_type)
      }

      if (residence_id) {
        query = query.eq('residence_id', residence_id)
      }

      if (folder_id) {
        query = query.eq('folder_id', folder_id)
      }

      if (owner_id) {
        query = query.eq('owner_id', owner_id)
      }

      if (unit_id) {
        query = query.eq('unit_id', unit_id)
      }

      if (document_year) {
        query = query.eq('document_year', document_year)
      }

      if (date_from) {
        query = query.gte('document_date', date_from)
      }

      if (date_to) {
        query = query.lte('document_date', date_to)
      }

      // Tag filter (array contains)
      if (tags && tags.length > 0) {
        query = query.contains('tags', tags)
      }

      // Text search (using full-text search on display_name)
      if (search && search.trim()) {
        query = query.textSearch('display_name', search, {
          type: 'websearch',
          config: 'french',
        })
      }

      // Apply sorting
      // When viewing documents in a folder, sort by manual sort_order first
      // Then apply user's chosen sort as secondary sort
      if (folder_id) {
        query = query.order('sort_order', { ascending: true })
      }

      const ascending = sort_direction === 'asc'
      query = query.order(sort_by, { ascending })

      // Apply pagination
      const from = (page - 1) * page_size
      const to = from + page_size - 1
      query = query.range(from, to)

      // Execute query
      const { data, error, count } = await query

      if (error) {
        throw error
      }

      const total_pages = count ? Math.ceil(count / page_size) : 0

      return {
        data: (data || []) as any as DocumentWithRelations[],
        count: count || 0,
        page,
        page_size,
        total_pages,
      }
    },
  })
}

/**
 * Fetch a single document by ID
 */
export function useDocument(documentId?: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, documentId],
    queryFn: async (): Promise<DocumentWithRelations | null> => {
      if (!documentId) return null

      const { data, error } = await supabase
        .from('documents')
        .select(
          `
          *,
          residences:residence_id(id, name, residence_key),
          owners:owner_id(id, first_name, last_name),
          units:unit_id(id, unit_number),
          uploader:uploaded_by(id, first_name, last_name)
        `
        )
        .eq('id', documentId)
        .single()

      if (error) throw error
      return data as any as DocumentWithRelations
    },
    enabled: !!documentId,
  })
}

/**
 * Fetch document versions
 */
export function useDocumentVersions(documentId?: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, documentId, 'versions'],
    queryFn: async (): Promise<DocumentWithRelations[]> => {
      if (!documentId) return []

      // TODO: Implement versioning when DB schema is ready
      // For now, return empty array
      return []
    },
    enabled: !!documentId,
  })
}

/**
 * Delete a document (soft delete) and recalculate sort_order for remaining documents
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (documentId: string) => {
      // Use RPC function to delete document and recalculate ordering
      const { data, error } = await (supabase.rpc as any)('delete_document_and_reorder', {
        p_document_id: documentId,
      })

      if (error) throw error

      // Check if the RPC call was successful
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to delete document')
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

/**
 * Update document metadata
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      documentId,
      updates,
    }: {
      documentId: string
      updates: DocumentUpdateInput
    }) => {
      const { data, error } = await supabase
        .from('documents')
        .update(updates as any)
        .eq('id', documentId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_QUERY_KEY, data.id] })
    },
  })
}

/**
 * Get document statistics
 */
export function useDocumentStats(residenceId?: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'stats', residenceId],
    queryFn: async () => {
      // TODO: Implement stats when DB schema is ready
      // For now, return empty stats
      return {
        total_documents: 0,
        total_size: 0,
        by_category: {},
        by_status: {},
        recent_uploads: 0,
        expiring_soon: 0,
      }
    },
  })
}

/**
 * Upload a document to Supabase Storage and create database record
 */
export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      file,
      metadata,
    }: {
      file: File
      metadata: {
        residence_id: string
        category?: string
        folder_id?: string
        document_type?: string
        display_name: string
        document_date?: string
        notes?: string
        owner_id?: string
        unit_id?: string
        tags?: string[]
      }
    }) => {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get agency_id from residence
      const { data: residence, error: residenceError } = await supabase
        .from('residences')
        .select('agency_id')
        .eq('id', metadata.residence_id)
        .single()

      if (residenceError || !residence) {
        throw new Error('Residence not found')
      }

      // Generate readable file path with timestamp
      // Format: residence_key/category/YYYYMMDD-HHMMSS_filename.ext
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)

      // Sanitize filename: remove special characters, keep only alphanumeric, dots, dashes, underscores
      const sanitizedFilename = file.name
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '-')
        .replace(/-+/g, '-') // Replace multiple dashes with single dash

      // Build path: residence_id/category/timestamp_filename
      const categoryFolder = metadata.category || 'autre'
      const fileName = `${metadata.residence_id}/${categoryFolder}/${timestamp}_${sanitizedFilename}`

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false,
        })

      if (uploadError) throw uploadError

      // Extract year from document date if provided
      const documentYear = metadata.document_date
        ? new Date(metadata.document_date).getFullYear()
        : null

      // Create database record
      // Note: created_by and updated_by are automatically set by database trigger
      const { data: documentData, error: dbError } = await supabase
        .from('documents')
        .insert({
          agency_id: residence.agency_id,
          residence_id: metadata.residence_id,
          folder_id: metadata.folder_id || null,
          category: metadata.category,
          document_type: metadata.document_type || null,
          display_name: metadata.display_name,
          original_filename: file.name,
          storage_path: fileName,
          storage_bucket: 'documents',
          mime_type: file.type,
          file_size: file.size,
          document_date: metadata.document_date || null,
          document_year: documentYear,
          notes: metadata.notes || null,
          owner_id: metadata.owner_id || null,
          unit_id: metadata.unit_id || null,
          tags: metadata.tags || [],
          status: 'active',
          origin: 'upload',
          confidentiality: 'public',
        } as any)
        .select()
        .single()

      if (dbError) {
        // If database insert fails, delete the uploaded file
        await supabase.storage.from('documents').remove([fileName])
        throw dbError
      }

      return documentData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

/**
 * Déplacer un document vers un autre dossier
 * Uses optimistic updates for instant UI feedback
 */
export function useMoveDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      documentId,
      folderId,
    }: {
      documentId: string
      folderId: string | null
    }) => {
      // Use RPC function to move document with proper sort_order handling
      const { data, error } = await (supabase.rpc as any)('move_document_to_folder', {
        p_document_id: documentId,
        p_new_folder_id: folderId,
      })

      if (error) throw error

      // Check if the RPC call was successful
      if (data && !data.success) {
        throw new Error(data.message || 'Failed to move document')
      }

      // Fetch the updated document to return it
      const { data: document, error: fetchError } = await supabase
        .from('documents')
        .select()
        .eq('id', documentId)
        .single()

      if (fetchError) throw fetchError
      return document
    },
    // Optimistic update: update UI immediately
    onMutate: async ({ documentId, folderId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: DOCUMENTS_QUERY_KEY })

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({ queryKey: DOCUMENTS_QUERY_KEY })

      // Optimistically update all matching queries
      queryClient.setQueriesData(
        { queryKey: DOCUMENTS_QUERY_KEY },
        (old: any) => {
          if (!old || typeof old !== 'object') return old
          if (!old.data || !Array.isArray(old.data)) return old

          // Update the document's folder_id in the list
          const updatedData = old.data.map((doc: any) =>
            doc.id === documentId ? { ...doc, folder_id: folderId } : doc
          )

          return {
            ...old,
            data: updatedData,
          }
        }
      )

      return { previousQueries }
    },
    // If mutation fails, rollback
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

/**
 * Count documents by residence
 * Returns a map of residence_id -> document count
 */
export function useDocumentCountByResidence(residenceIds?: string[]) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'count-by-residence', residenceIds],
    queryFn: async (): Promise<Record<string, number>> => {
      if (!residenceIds || residenceIds.length === 0) {
        return {}
      }

      const { data, error } = await supabase
        .from('documents')
        .select('residence_id')
        .in('residence_id', residenceIds)
        .eq('status', 'active')
        .is('deleted_at', null)

      if (error) throw error

      // Count documents per residence
      const counts: Record<string, number> = {}
      residenceIds.forEach((id) => {
        counts[id] = 0
      })

      data?.forEach((doc) => {
        if (doc.residence_id) {
          counts[doc.residence_id] = (counts[doc.residence_id] || 0) + 1
        }
      })

      return counts
    },
    enabled: !!residenceIds && residenceIds.length > 0,
  })
}

/**
 * Count documents by folder
 * Returns a map of folder_id -> document count
 */
export function useDocumentCountByFolder(residenceId?: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'count-by-folder', residenceId],
    queryFn: async (): Promise<Record<string, number>> => {
      if (!residenceId) {
        return {}
      }

      const { data, error } = await supabase
        .from('documents')
        .select('folder_id')
        .eq('residence_id', residenceId)
        .eq('status', 'active')
        .is('deleted_at', null)

      if (error) throw error

      // Count documents per folder
      const counts: Record<string, number> = {}

      data?.forEach((doc) => {
        if (doc.folder_id) {
          counts[doc.folder_id] = (counts[doc.folder_id] || 0) + 1
        }
      })

      return counts
    },
    enabled: !!residenceId,
  })
}

/**
 * Download a document from Supabase Storage
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (document: DocumentWithRelations) => {
      // Get signed URL for download (valid for 60 seconds)
      const { data, error } = await supabase.storage
        .from(document.storage_bucket)
        .createSignedUrl(document.storage_path, 60)

      if (error) throw error
      if (!data?.signedUrl) throw new Error('Failed to generate download URL')

      // Trigger download in browser
      const response = await fetch(data.signedUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = document.original_filename
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return { success: true }
    },
  })
}

/**
 * Reorder documents within a folder
 * Pass document IDs in the new desired order
 * Uses optimistic updates for instant UI feedback
 */
export function useReorderDocuments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      documentIds,
      folderId,
    }: {
      documentIds: string[]
      folderId: string
    }) => {
      const { data, error } = await (supabase.rpc as any)('reorder_documents', {
        p_document_ids: documentIds,
        p_folder_id: folderId,
      })

      if (error) throw error
      return data
    },
    // Optimistic update: update UI immediately before server responds
    onMutate: async ({ documentIds }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: DOCUMENTS_QUERY_KEY })

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({ queryKey: DOCUMENTS_QUERY_KEY })

      // Optimistically update all matching queries
      queryClient.setQueriesData(
        { queryKey: DOCUMENTS_QUERY_KEY },
        (old: any) => {
          // Safety checks: ensure old has the expected structure
          if (!old || typeof old !== 'object') return old
          if (!old.data || !Array.isArray(old.data)) return old

          // Create a map for quick lookup of new sort order
          const orderMap = new Map(documentIds.map((id, index) => [id, index]))

          // Sort documents by their new order
          const reorderedData = [...old.data].sort((a, b) => {
            const aOrder = orderMap.get(a.id) ?? Infinity
            const bOrder = orderMap.get(b.id) ?? Infinity
            return aOrder - bOrder
          })

          return {
            ...old,
            data: reorderedData,
          }
        }
      )

      // Return context with previous data for rollback
      return { previousQueries }
    },
    // If mutation fails, rollback to previous state
    onError: (error, _variables, context) => {
      if (context?.previousQueries) {
        // Restore all previous query states
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to reorder documents:', error)
    },
    // Always refetch after error or success to ensure we're in sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}
